import { useState, useEffect } from 'react';
import { useUserRoles } from './useUserRoles';
import { useAuth } from './useAuth';

interface MagicLinkBookingAccess {
  hasAccess: boolean;
  isLoading: boolean;
  bookingId: string | null;
  tokenType: string | null;
  error: string | null;
}

/**
 * Hook to handle magic link booking access
 * Extracts booking info from URL and validates user permissions
 */
export const useMagicLinkBookingAccess = (): MagicLinkBookingAccess => {
  const { user, loading: authLoading } = useAuth();
  const { roles, isLoading: rolesLoading } = useUserRoles();
  
  const [state, setState] = useState<MagicLinkBookingAccess>({
    hasAccess: false,
    isLoading: true,
    bookingId: null,
    tokenType: null,
    error: null
  });

  useEffect(() => {
    // Wait for auth and roles to load
    if (authLoading || rolesLoading) {
      return;
    }

    // Check if user came via magic link
    const urlParams = new URLSearchParams(window.location.search);
    const bookingId = window.location.hash.slice(1); // Get booking ID from hash
    
    // If no booking ID in hash, no magic link access needed
    if (!bookingId) {
      setState({
        hasAccess: true, // Normal authenticated access
        isLoading: false,
        bookingId: null,
        tokenType: null,
        error: null
      });
      return;
    }

    // Check if user is authenticated (magic link should have authenticated them)
    if (!user) {
      setState(prev => ({
        ...prev,
        hasAccess: false,
        isLoading: false,
        error: 'Authentication required to access this booking'
      }));
      return;
    }

    // Extract token type from user metadata (set during magic link generation)
    const tokenType = user.user_metadata?.token_type || 'customer';
    
    // Validate access based on token type and user roles
    let hasValidAccess = false;
    
    switch (tokenType) {
      case 'customer':
        // Customers always have access to bookings they can see
        hasValidAccess = true;
        break;
        
      case 'org_admin':
        hasValidAccess = roles.isOrgAdmin;
        break;
        
      case 'platform_admin':
        hasValidAccess = roles.isPlatformAdmin;
        break;
        
      case 'support':
        hasValidAccess = roles.isAdmin; // Support users should have admin flag
        break;
        
      default:
        hasValidAccess = false;
    }

    setState({
      hasAccess: hasValidAccess,
      isLoading: false,
      bookingId,
      tokenType,
      error: hasValidAccess ? null : `Insufficient permissions for ${tokenType} access`
    });

  }, [user, roles, authLoading, rolesLoading]);

  return state;
};